<template>
  <div class="manage-entries" :class="{ 'my-3': !modalOpts }">
    <b-breadcrumb class="align-items-center">
      <b-breadcrumb-item active v-if="modalOpts">
        <b-btn @click.prevent="() => $emit('modal-selected', previousEntries).$emit('modal-close')" size="sm" variant="transparent">
          <font-awesome-icon icon="times" title="Discard"/> <span class="sr-only">Discard</span>
        </b-btn>

        Select {{ modalOpts.title }}
      </b-breadcrumb-item>
      <template v-else>
        <b-breadcrumb-item :to="{ name: 'ManageAll' }">Manage</b-breadcrumb-item>
        <b-breadcrumb-item active>
          <template v-if="listSchema && listSchema.page && listSchema.page.breadcrumb">
            <template v-if="typeof listSchema.page.breadcrumb === 'function'">{{ listSchema.page.breadcrumb.call(this) }}</template>
            <template v-else>{{ listSchema.page.breadcrumb }}</template>
          </template>
          <template v-else-if="modelData && modelData.name">
            {{ modelData.name }}
          </template>
          <template v-else>Entries</template>
        </b-breadcrumb-item>
      </template>
      <div class="flex-fill"/>
      <template v-if="whereOptions">
        <b-btn class="mr-1 d-block d-md-none text-decoration-none text-primary" size="sm" variant="link"
          @click="() => showWhere = !showWhere">
            <font-awesome-icon icon="filter" title="Filter"/>
        </b-btn>
        <b-dropdown class="mr-1 d-none d-md-block text-decoration-none" no-caret right size="sm" variant="link"
          :toggle-class="`${where && Object.keys(where).length ? 'text-primary' : 'text-muted'}`" menu-class="p-0">
          <template v-slot:button-content>Filters <font-awesome-icon icon="filter" title="Filter"/></template>
          <b-dropdown-text class="px-0" :style="{ 'min-width': '500px' }">
            <template v-if="modelData && modelData.icon && modelData.name">
              Filters for <strong><font-awesome-icon :icon="modelData.icon"/> {{ modelData.name }}</strong>
            </template>
            <manage-filters :options="whereOptions" v-model="where"/>
          </b-dropdown-text>
        </b-dropdown>
      </template>
      <template v-if="modalOpts">
        <b-btn @click.prevent="$emit('modal-close')" size="sm" variant="outline-success">
          <font-awesome-icon icon="check" title="Discard"/> Save
        </b-btn>
      </template>
      <b-btn v-else size="sm" :to="{ name: 'ManageEntryView', params: { entryType, entryID: 'new' } }" variant="outline-primary">
        <span class="d-none d-md-inline">New</span> <font-awesome-icon icon="plus" title="New"/>
      </b-btn>
    </b-breadcrumb>

    <b-card class="mb-3 d-block d-md-none" body-class="p-2" v-if="whereOptions && showWhere">
      <template v-if="modelData && modelData.icon && modelData.name">
        Filters for <strong><font-awesome-icon :icon="modelData.icon"/> {{ modelData.name }}</strong>
      </template>
      <manage-filters :options="whereOptions" v-model="where"/>
    </b-card>

    <b-card class="mb-3 border-0" body-class="p-0">
      <b-input placeholder="Quick search" v-model="searchTerm" />
    </b-card>

    <b-table ref="manageTable" id="manageTable" hover responsive show-empty sticky-header bordered stacked="sm"
      class="bg-white mb-3" table-class="m-0" :busy="!listEntries" :primary-key="primaryKey"
      :fields="listColumns" :items="items"
      :current-page="page" :per-page="limit" @row-clicked="toggleSelected"
      :tbody-tr-class="getRowClass"
      :selectable="Boolean(modalOpts)" :select-mode="modalOpts && modalOpts.many ? 'multi' : 'single'">
        <template v-slot:table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong>Loading...</strong>
          </div>
        </template>

        <template v-slot:cell(title)="{ value, item }">
          <router-link v-if="canClickRow(item)" :to="{ name: 'ManageEntryView', params: { entryType, entryID: item.id } }">{{ value }}</router-link>
          <template v-else>{{ value }}</template>
        </template>

        <template v-slot:cell(department)="{ value: { id, name } }">
          <template v-if="id">{{ name }}</template>
          <span v-else class="text-muted">(Not set)</span>
        </template>
        <template v-slot:cell(location)="{ value: { id, name, countryCode } }">
          <template v-if="id">{{ name }}{{ countryCode ? ` (${countryCode})` :'' }}</template>
          <span v-else class="text-muted">(Not set)</span>
        </template>
    </b-table>

    <div class="d-flex flex-column justify-content-center align-items-center" v-if="totalPages > 1">
      <b-pagination v-if="listEntries" v-model="page" :per-page="limit" :total-rows="items.length"/>
    </div>
  </div>
</template>

<script>
import _isPlainObject from 'lodash/isPlainObject';
import assert from 'http-assert-plus';
import ManageFilters from '@/components/manage/Filters';
import models from '@/models';
import ms from 'ms';

export default {
  name: 'ManageList',
  props: {
    entryType: {
      type: String,
      validate: key => models.hasOwnProperty(key),
    },
    modalOpts: {
      type: Object,
      default: () => null,
    },
  },
  components: {
    ManageFilters,
  },
  data() {
    const { listSchema: { where = null, order = null, limit } = {} } = models[this.entryType] || {};
    const { where: modalWhere, limit: modalLimit, value } = this.modalOpts || {};

    const previous = Array.isArray(value) && value.length ? value.reduce((l, entry) => l.concat(entry), []) : [];

    return {
      where: modalWhere || where || {},
      order,
      page: 1,
      limit: modalLimit || limit || 500,
      showWhere: false,
      previousEntries: previous,
      selectedEntries: previous,
      searchTerm: null,
    };
  },
  apollo: {
    listEntries: {
      query() {
        const { query } = this.listSchema || {};
        assert(query, new Error('Expected apollo.query to be a graphql-tag or string in listSchema'));
        return query;
      },
      variables() {
        const { where, order } = this;
        return { where, order };
      },
      update(data) {
        const { update } = this.listSchema || {};
        assert(typeof update === 'function', new Error('Expected update to be a function in listSchema'));
        return update.call(this, data);
      },
      async result({ loading, data, errors }) {
        const { result } = this.listSchema || {};
        if (!loading && data && typeof result === 'function') {
          await result.call(this, { data, errors });
        }

        const hasValue = this.modalOpts && this.$refs.manageTable.selectable && Boolean(
          (this.modalOpts.many && Array.isArray(this.modalOpts.value) && this.modalOpts.value.length) ||
          (this.modalOpts.value && this.modalOpts.value.id));

        if (hasValue && this.listEntries && Array.isArray(this.listEntries.edges) && this.listEntries.edges.length) {
          this.$nextTick(() => this.$refs.manageTable.selectedRows = this.selectedRowMap);
        }
      },
      error(err) {
        this.$notifyErr(err);
      },
      deep: true,
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
      throttle: ms('1s'),
    },
  },
  metaInfo() {
    const meta = {
      title: 'Manage Entries',
    };

    const { listSchema, modelData } = this;
    if (listSchema && listSchema.page && listSchema.page.title) {
      meta.title = listSchema.page.title;
    } else if (modelData && modelData.name) {
      meta.title = `Manage ${modelData.name}`;
    }

    return meta;
  },
  computed: {
    modelData() {
      const { [this.entryType]: modelData = null } = models || {};
      return modelData;
    },
    listSchema() {
      const { listSchema = null } = this.modelData || {};
      return listSchema;
    },
    whereOptions() {
      const { whereOptions = null } = this.listSchema || {};
      return whereOptions;
    },
    listColumns() {
      const { columns } = this.listSchema || {};
      if (typeof columns === 'function') {
        return columns.call(this);
      } else {
        return columns;
      }
    },
    listColumnOptions() {
      const { columnOptions } = this.listSchema || {};
      return Object.keys(columnOptions).map(key => ({ key, ...columnOptions[key] }));
    },
    bulkSchema() {
      const { bulkSchema = null } = this.modelData || {};
      return bulkSchema;
    },
    items() {
      const { searchTerm, listEntries } = this;
      return (listEntries && Array.isArray(listEntries.edges) ? listEntries.edges : []).filter(el => {
        const { title, name, department, location } = el;
        if (searchTerm && searchTerm.length > 1) {
          let result = false;
          if (title && title.length) {
            result = result || title.toLowerCase().includes(searchTerm.toLowerCase());
          }
          if (name && name.length) {
            result = result || name.toLowerCase().includes(searchTerm.toLowerCase());
          }
          if (location && location.name && location.name.length) {
            result = result || location.name.toLowerCase().includes(searchTerm.toLowerCase());
          }
          if (department && department.name && department.name.length) {
            result = result || department.name.toLowerCase().includes(searchTerm.toLowerCase());
          }
          return result;
        }
        return true;
      });
    },
    orderOptions() {
      const { orderOptions } = this.listSchema || {};
      if (typeof orderOptions === 'function') {
        return orderOptions.call(this);
      } else if (Array.isArray(orderOptions) || _isPlainObject(orderOptions)) {
        return orderOptions;
      } else {
        return null;
      }
    },
    selectedRowMap() {
      const { edges } = { ...this.listEntries };
      return (Array.isArray(edges) ? edges : []).slice((this.page * this.limit) - this.limit, this.page * this.limit)
        .map(({ id }) => Boolean(this.selectedEntries.find(e => e && e.id === id)));
    },
    primaryKey() {
      const { primaryKey } = this.listEntries || {};
      return primaryKey || null;
    },
    total() {
      const { total } = this.listEntries || {};
      return total || 0;
    },
    totalPages() {
      return this.limit < this.total ? Math.ceil(this.total / (this.limit || 1)) || 1 : 1;
    },
  },
  methods: {
    canClickRow(item) {
      if (this.modalOpts) {
        return false;
      } else {
        const { canClickRow } = this.listSchema || {};
        if (typeof canClickRow === 'function') {
          return canClickRow.call(this, item);
        } else {
          return true;
        }
      }
    },
    toggleSelected(item) {
      const i = this.selectedEntries.findIndex(e => e && e.id === item.id);

      if (i > -1) {
        this.selectedEntries.splice(i, 1);
      } else {
        this.selectedEntries.push(item);
      }

      this.$emit('modal-selected', this.selectedEntries);
    },
    getRowClass(row) {
      const { selectedEntries } = this;
      return selectedEntries.map(e => (e || {}).id).includes((row || {}).id) ? 'b-table-row-selected table-active' : '';
    }
  },
  watch: {
    page() {
      this.$scrollTo('body');
      this.$nextTick(() => this.$refs.manageTable.selectedRows = this.selectedRowMap);
    },
  },
};
</script>

<style lang="scss">
.manage-entries {
  .table {
    th, td {
      &.w-25px {
        width: 25px;
      }
      &.w-100px {
        width: 100px;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/manage/list.vue